import React from 'react';
import { styled } from '@linaria/react';
import { Play, Pause } from 'lucide-react';

import useBrowser from '@/hooks/use-browser';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  status: 'idle' | 'playing' | 'paused' | 'done';
  isDisabled?: boolean;
  isGlowing?: boolean;
}

function GlowingPlayButton({
  status,
  isDisabled,
  isGlowing,
  ...delegated
}: Props) {
  const buttonStyles = isDisabled
    ? {
        '--default-y': '-2px',
        '--hover-y': '-2px',
        '--active-y': '-2px',
        filter: 'grayscale(100%) brightness(50%)',
      }
    : {
        '--default-y': status === 'playing' ? '-2px' : '-4px',
        '--hover-y': status === 'playing' ? '-2px' : '-6px',
        '--active-y': '0px',
      };

  const browser = useBrowser();

  return (
    // The glow breaks on Safari, since filters are funky there
    <Wrapper
      data-is-glowing={String(
        Boolean(browser !== 'safari' && isGlowing)
      )}
    >
      <Button {...delegated} style={buttonStyles}>
        <Shaft />
        <Front>
          {status === 'playing' ? (
            <Pause
              size={32}
              style={{ transform: 'translate(0px, -2px)' }}
            />
          ) : (
            <Play
              size={32}
              style={{ transform: 'translate(2px, -2px)' }}
            />
          )}
        </Front>
      </Button>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: var(--play-button-size, 56px);
  height: var(--play-button-size, 56px);
  border-radius: 100px;
  background: linear-gradient(
    to top,
    var(--color-gray-600),
    var(--color-background)
  );
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    background: var(--color-background);
    border-radius: 1000px;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 1000px;
    filter: blur(12px);
    z-index: -1;
  }

  &[data-is-glowing='true']::after {
    animation: playGlowEffect 1000ms infinite alternate ease-in-out;

    @keyframes playGlowEffect {
      from {
        background: hsl(150deg 100% 60% / 0.3);
      }
      to {
        background: hsl(150deg 100% 60% / 0.9);
      }
    }
  }
`;

const Button = styled.button`
  position: absolute;
  inset: 0;
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  margin: auto;
  border-radius: 1000px; /* for the focus outline */
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  &:focus-visible {
    outline-offset: 6px;
    outline-color: hsl(150deg 100% 60%);
  }

  filter: brightness(95%) contrast(1);
  transition: filter 300ms;

  &:hover {
    filter: brightness(105%) contrast(1.05);
  }

  ${Wrapper}[data-is-glowing='true'] & {
    animation: glow 1000ms infinite alternate ease-in-out;

    @keyframes glow {
      from {
        filter: brightness(95%) contrast(0.9);
      }
      to {
        filter: brightness(120%) contrast(1.2);
      }
    }
  }
`;

const Front = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 100px;
  color: hsl(150deg 100% 28%);
  background: hsl(150deg 100% 60%);
  background: radial-gradient(
    circle at 50% 50%,
    hsl(150deg 100% 60%) 0%,
    hsl(150deg 100% 60%) 65%,
    hsl(150deg 100% 30%) 70%,
    hsl(150deg 100% 30%) 100%
  );
  overflow: hidden;
  transform: translateY(var(--default-y));
  transition: transform 400ms cubic-bezier(0.17, 0.67, 0.5, 1);

  ${Button}:hover & {
    transform: translateY(var(--hover-y));
    transition: transform 400ms;
  }

  ${Button}:active & {
    transform: translateY(var(--active-y));
    transition: transform 0ms;
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 8px;
    right: 8px;
    height: calc(50% - 8px);
    background: white;
    border-radius: 1000px 1000px 10px 10px;
    filter: blur(8px);
    opacity: 0.6;
    transition: all 200ms;

    ${Button}:hover & {
      opacity: 1;
    }
  }

  svg {
    display: block;
  }
`;

const Shaft = styled.span`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 100px;
  background: hsl(150deg 100% 30%);
  background: radial-gradient(
    circle at 50% 40%,
    hsl(150deg 100% 30%) 0% 65%,
    hsl(150deg 100% 10%) 80% 100%
  );
`;

export default GlowingPlayButton;
